import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { CreditsProvider } from "./contexts/CreditsContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminRoute } from "./components/AdminRoute";
import { ScrollToTop } from "./components/ScrollToTop";
import { MetaPixelTracker } from "./components/MetaPixelTracker";

// Eager load - needed for initial page
import LandingPage from "./pages/LandingPage";
import Auth from "./pages/Auth";
import Gallery from "./pages/Gallery";

// Lazy load - not needed on initial page load
const GeneratorPage = lazy(() => import("./pages/GeneratorPage"));
const VideoGeneratorPage = lazy(() => import("./pages/VideoGeneratorPage"));
const AudioGeneratorPage = lazy(() => import("./pages/AudioGeneratorPage"));
const VideoUpscalePage = lazy(() => import("./pages/VideoUpscalePage"));
const EditPage = lazy(() => import("./pages/EditPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const DmcaPolicy = lazy(() => import("./pages/DmcaPolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

const queryClient = new QueryClient();

// Loading fallback component - Premium Branding
const PageLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-6">
    <div 
      className="animate-spin rounded-full h-12 w-12 border-b-2" 
      style={{ borderColor: 'hsl(var(--gold))' }} 
    />
    <span 
      className="text-sm font-medium animate-pulse bg-clip-text text-transparent"
      style={{
        backgroundImage: 'linear-gradient(135deg, hsl(45 100% 51%), hsl(0 72% 51%))'
      }}
    >
      Lovers Labs: World's Best Uncensored AI Generator
    </span>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <MetaPixelTracker />
        <AuthProvider>
          <CreditsProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute><AdminRoute><AdminDashboard /></AdminRoute></ProtectedRoute>} />
                <Route path="/generator" element={<ProtectedRoute><GeneratorPage /></ProtectedRoute>} />
                <Route path="/video-generator" element={<ProtectedRoute><VideoGeneratorPage /></ProtectedRoute>} />
                <Route path="/audio-generator" element={<ProtectedRoute><AdminRoute><AudioGeneratorPage /></AdminRoute></ProtectedRoute>} />
                <Route path="/video-upscale" element={<ProtectedRoute><VideoUpscalePage /></ProtectedRoute>} />
                <Route path="/edit" element={<ProtectedRoute><EditPage /></ProtectedRoute>} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/termsofservice" element={<TermsOfService />} />
                <Route path="/privacypolicy" element={<PrivacyPolicy />} />
                <Route path="/dmcapolicy" element={<DmcaPolicy />} />
                <Route path="/refund" element={<RefundPolicy />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </CreditsProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
